export interface Point {
  x: number;
  y: number;
}

export class ViewportController {
  private canvas: HTMLCanvasElement;
  private container: HTMLElement;
  private scale: number = 1.0;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private minScale: number = 0.5;
  private maxScale: number = 3.0;

  private isPanning: boolean = false;
  private panStartX: number = 0;
  private panStartY: number = 0;
  private lastTouchDistance: number = 0;

  private onTransformChange?: () => void;
  private onScaleChange?: (scale: number) => void;

  private boundWheel: (event: WheelEvent) => void;
  private boundMouseDown: (event: MouseEvent) => void;
  private boundMouseMove: (event: MouseEvent) => void;
  private boundMouseUp: () => void;
  private boundTouchStart: (event: TouchEvent) => void;
  private boundTouchMove: (event: TouchEvent) => void;
  private boundTouchEnd: () => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.container = canvas.parentElement || document.body;

    this.boundWheel = this.handleWheel.bind(this);
    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundTouchStart = this.handleTouchStart.bind(this);
    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.boundTouchEnd = this.handleTouchEnd.bind(this);

    this.attachEventListeners();
    this.applyTransform();
  }

  private attachEventListeners(): void {
    this.container.addEventListener('wheel', this.boundWheel, { passive: false });
    this.container.addEventListener('mousedown', this.boundMouseDown);
    this.container.addEventListener('mousemove', this.boundMouseMove);
    this.container.addEventListener('mouseup', this.boundMouseUp);
    this.container.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    this.container.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    this.container.addEventListener('touchend', this.boundTouchEnd);
  }

  private handleWheel(event: WheelEvent): void {
    event.preventDefault();

    const delta = -event.deltaY;
    const zoomFactor = 1.1;
    const oldScale = this.scale;
    const newScale = delta > 0
      ? Math.min(this.scale * zoomFactor, this.maxScale)
      : Math.max(this.scale / zoomFactor, this.minScale);

    if (newScale === oldScale) return;

    const rect = this.container.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    this.offsetX = mouseX - (mouseX - this.offsetX) * (newScale / oldScale);
    this.offsetY = mouseY - (mouseY - this.offsetY) * (newScale / oldScale);
    this.scale = newScale;

    this.applyTransform();
    this.onScaleChange?.(this.scale);
    this.onTransformChange?.();
  }

  private handleMouseDown(event: MouseEvent): void {
    if (event.button === 1 || (event.button === 0 && event.ctrlKey)) {
      event.preventDefault();
      this.isPanning = true;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    if (this.isPanning) {
      const dx = event.clientX - this.panStartX;
      const dy = event.clientY - this.panStartY;
      this.offsetX += dx;
      this.offsetY += dy;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;

      this.applyTransform();
      this.onTransformChange?.();
    }
  }

  private handleMouseUp(): void {
    this.isPanning = false;
  }

  private handleTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      event.preventDefault();
      this.lastTouchDistance = this.getTouchDistance(event.touches);
    } else if (event.touches.length === 1) {
      this.panStartX = event.touches[0].clientX;
      this.panStartY = event.touches[0].clientY;
    }
  }

  private handleTouchMove(event: TouchEvent): void {
    if (event.touches.length === 2) {
      event.preventDefault();
      const distance = this.getTouchDistance(event.touches);
      const scaleFactor = distance / this.lastTouchDistance;
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * scaleFactor));

      const rect = this.canvas.getBoundingClientRect();
      const centerX = (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
      const centerY = (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;

      this.offsetX = centerX - (centerX - this.offsetX) * (newScale / this.scale);
      this.offsetY = centerY - (centerY - this.offsetY) * (newScale / this.scale);
      this.scale = newScale;
      this.lastTouchDistance = distance;

      this.applyTransform();
      this.onTransformChange?.();
    } else if (event.touches.length === 1 && !event.ctrlKey) {
      const dx = event.touches[0].clientX - this.panStartX;
      const dy = event.touches[0].clientY - this.panStartY;
      this.offsetX += dx;
      this.offsetY += dy;
      this.panStartX = event.touches[0].clientX;
      this.panStartY = event.touches[0].clientY;

      this.applyTransform();
      this.onTransformChange?.();
    }
  }

  private handleTouchEnd(): void {
    this.lastTouchDistance = 0;
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  applyTransform(): void {
    this.canvas.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`;
    this.canvas.style.transformOrigin = '0 0';
  }

  resetTransform(): void {
    this.scale = 1.0;
    this.offsetX = 0;
    this.offsetY = 0;
    this.canvas.style.transform = 'none';
    this.canvas.style.transformOrigin = 'center';
  }

  screenToCanvas(screenX: number, screenY: number): Point {
    const rect = this.canvas.getBoundingClientRect();
    // relX/relY are in CSS transformed space (includes scale)
    const relX = screenX - rect.left;
    const relY = screenY - rect.top;
    // Divide by scale to get logical coordinates (what renderers expect)
    const x = relX / this.scale;
    const y = relY / this.scale;
    return { x, y };
  }

  getScale(): number {
    return this.scale;
  }

  getOffset(): Point {
    return { x: this.offsetX, y: this.offsetY };
  }

  onTransformChangeCallback(callback: () => void): void {
    this.onTransformChange = callback;
  }

  onScaleChangeCallback(callback: (scale: number) => void): void {
    this.onScaleChange = callback;
  }

  destroy(): void {
    this.container.removeEventListener('wheel', this.boundWheel);
    this.container.removeEventListener('mousedown', this.boundMouseDown);
    this.container.removeEventListener('mousemove', this.boundMouseMove);
    this.container.removeEventListener('mouseup', this.boundMouseUp);
    this.container.removeEventListener('touchstart', this.boundTouchStart);
    this.container.removeEventListener('touchmove', this.boundTouchMove);
    this.container.removeEventListener('touchend', this.boundTouchEnd);
    this.resetTransform();
  }
}
